import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  buildPerformanceAxisTickIndices,
  formatPerformanceAxisDate,
  formatPerformanceTooltipDate,
  maxAxisLabelsForChartWidth,
} from '@/utils/performanceChartAxisTicks';
import {
  buildAreaPath,
  computeAxisMax,
  computeNiceAxisTicks,
  monotoneCubicPath,
  scaleValue,
} from '@/utils/chartPaths';
import {
  formatPerformanceAxisValue,
  formatPerformanceTooltipValue,
  performanceMetricDef,
  type PerformanceDataPoint,
  type PerformanceMetricId,
} from '@/utils/performanceChartMetrics';

const CHART_HEIGHT = 300;
const MARGIN = { top: 20, right: 56, bottom: 44, left: 72 };
const PLOT_HEIGHT = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;
const TOOLTIP_WIDTH = 220;
const TOOLTIP_HEIGHT = 96;
const TOOLTIP_GAP = 12;
const TOOLTIP_PAD = 4;

type PerformanceMetricsChartProps = {
  points: PerformanceDataPoint[];
  visibleMetricIds: PerformanceMetricId[];
  loading?: boolean;
  error?: boolean;
  chartWidth?: number;
};

function positionTooltip(
  chartWidth: number,
  pointX: number,
  pointY: number
): { x: number; y: number } {
  const maxX = chartWidth - TOOLTIP_WIDTH - TOOLTIP_PAD;
  const maxY = CHART_HEIGHT - TOOLTIP_HEIGHT - TOOLTIP_PAD;

  let x = pointX + TOOLTIP_GAP;
  if (x > maxX) x = pointX - TOOLTIP_GAP - TOOLTIP_WIDTH;
  x = Math.min(maxX, Math.max(TOOLTIP_PAD, x));

  let y = pointY - TOOLTIP_HEIGHT - TOOLTIP_GAP;
  if (y < TOOLTIP_PAD) y = pointY + TOOLTIP_GAP;
  y = Math.min(maxY, Math.max(TOOLTIP_PAD, y));

  return { x, y };
}

function buildMetricPath(
  points: PerformanceDataPoint[],
  metricId: PerformanceMetricId,
  xForIndex: (index: number) => number,
  yForValue: (value: number) => number
): string {
  const coords = points.map((point, index) => ({
    x: xForIndex(index),
    y: yForValue(point[metricId]),
  }));
  return monotoneCubicPath(coords);
}

export function PerformanceMetricsChart({
  points,
  visibleMetricIds,
  loading = false,
  error = false,
  chartWidth: chartWidthProp,
}: PerformanceMetricsChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(880);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width && Number.isFinite(width)) setContainerWidth(width);
    });
    observer.observe(node);
    setContainerWidth(node.clientWidth || 880);
    return () => observer.disconnect();
  }, []);

  const chartWidth = chartWidthProp ?? Math.max(640, containerWidth);
  const plotWidth = chartWidth - MARGIN.left - MARGIN.right;

  const visibleMetrics = useMemo(
    () => visibleMetricIds.map((id) => performanceMetricDef(id)),
    [visibleMetricIds]
  );

  const scales = useMemo(() => {
    const salesValues = points.map((point) => point.sales);
    const ordersValues = points.map((point) => point.orders);
    const spendValues = points.map((point) => point.spend);
    return {
      salesMax: computeAxisMax(salesValues),
      ordersMax: computeAxisMax(ordersValues),
      spendMax: computeAxisMax(spendValues),
    };
  }, [points]);

  const xForIndex = (index: number) =>
    points.length <= 1 ? plotWidth / 2 : (index / (points.length - 1)) * plotWidth;

  const yForSales = (value: number) => scaleValue(value, 0, scales.salesMax, PLOT_HEIGHT);
  const yForOrders = (value: number) => scaleValue(value, 0, scales.ordersMax, PLOT_HEIGHT);
  const yForSpend = (value: number) => scaleValue(value, 0, scales.spendMax, PLOT_HEIGHT);

  const yForMetric = (metricId: PerformanceMetricId, value: number) => {
    if (metricId === 'sales') return yForSales(value);
    if (metricId === 'orders') return yForOrders(value);
    return yForSpend(value);
  };

  const axisTickIndices = useMemo(
    () => buildPerformanceAxisTickIndices(points.length, maxAxisLabelsForChartWidth(chartWidth)),
    [points.length, chartWidth]
  );

  const salesTicks = computeNiceAxisTicks(0, scales.salesMax);
  const ordersTicks = computeNiceAxisTicks(0, scales.ordersMax);
  const spendTicks = computeNiceAxisTicks(0, scales.spendMax);

  const hoveredPoint = hoveredIndex === null ? null : points[hoveredIndex] ?? null;
  let tooltipPos: { x: number; y: number } | null = null;
  if (hoveredPoint && hoveredIndex !== null) {
    const pointX = MARGIN.left + xForIndex(hoveredIndex);
    const ys = visibleMetricIds.map((metricId) =>
      MARGIN.top + yForMetric(metricId, hoveredPoint[metricId])
    );
    const pointY = ys.length ? Math.min(...ys) : MARGIN.top + PLOT_HEIGHT / 2;
    tooltipPos = positionTooltip(chartWidth, pointX, pointY);
  }

  const hasData = points.length > 0;

  return (
    <div ref={containerRef} className="w-full">
      {loading ? (
        <p className="text-sm text-foreground-subtle flex items-center gap-2 py-8 justify-center">
          <Loader2 className="w-4 h-4 animate-spin text-brand" />
          Loading chart…
        </p>
      ) : null}

      {!loading && error ? (
        <p className="text-sm text-error py-6">Failed to load performance data. Please try again.</p>
      ) : null}

      {!loading && !error && !hasData ? (
        <p className="text-sm text-foreground-subtle py-6">
          No performance data for this period.
        </p>
      ) : null}

      {!loading && !error && hasData ? (
        <div className="overflow-x-auto">
          <svg
            viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
            className="w-full min-w-[640px] h-auto"
            role="img"
            aria-label="Campaign performance line chart for sales, orders, and spend"
          >
            <defs>
              {visibleMetrics.map((metric) => (
                <linearGradient
                  key={`gradient-${metric.id}`}
                  id={`performance-fill-${metric.id}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={metric.color} stopOpacity="0.18" />
                  <stop offset="100%" stopColor={metric.color} stopOpacity="0.02" />
                </linearGradient>
              ))}
            </defs>

            <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
              {visibleMetricIds.includes('sales')
                ? salesTicks.map((tick) => {
                    const y = yForSales(tick);
                    return (
                      <line
                        key={`grid-sales-${tick}`}
                        x1={0}
                        y1={y}
                        x2={plotWidth}
                        y2={y}
                        stroke="rgb(229 231 235)"
                        strokeWidth={1}
                      />
                    );
                  })
                : visibleMetricIds.includes('spend')
                  ? spendTicks.map((tick) => {
                      const y = yForSpend(tick);
                      return (
                        <line
                          key={`grid-spend-${tick}`}
                          x1={0}
                          y1={y}
                          x2={plotWidth}
                          y2={y}
                          stroke="rgb(229 231 235)"
                          strokeWidth={1}
                        />
                      );
                    })
                  : ordersTicks.map((tick) => {
                      const y = yForOrders(tick);
                      return (
                        <line
                          key={`grid-orders-${tick}`}
                          x1={0}
                          y1={y}
                          x2={plotWidth}
                          y2={y}
                          stroke="rgb(229 231 235)"
                          strokeWidth={1}
                        />
                      );
                    })}

              {visibleMetrics.map((metric) => {
                const linePath = buildMetricPath(points, metric.id, xForIndex, (value) =>
                  yForMetric(metric.id, value)
                );
                if (!linePath) return null;
                const firstX = xForIndex(0);
                const lastX = xForIndex(points.length - 1);
                const areaPath = buildAreaPath(linePath, PLOT_HEIGHT, firstX, lastX);
                return (
                  <g key={`series-${metric.id}`}>
                    <path d={areaPath} fill={`url(#performance-fill-${metric.id})`} stroke="none" />
                    <path
                      d={linePath}
                      fill="none"
                      stroke={metric.color}
                      strokeWidth={2}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                );
              })}

              {points.map((point, index) => {
                const x = xForIndex(index);
                return (
                  <rect
                    key={`hover-${point.date}`}
                    x={x - plotWidth / Math.max(points.length * 2, 2)}
                    y={0}
                    width={plotWidth / Math.max(points.length, 1)}
                    height={PLOT_HEIGHT}
                    fill="transparent"
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  />
                );
              })}

              {axisTickIndices.map((index) => {
                const point = points[index];
                if (!point) return null;
                const x = xForIndex(index);
                return (
                  <text
                    key={`x-label-${point.date}`}
                    x={x}
                    y={PLOT_HEIGHT + 18}
                    textAnchor="middle"
                    className="fill-foreground-subtle text-[11px]"
                  >
                    {formatPerformanceAxisDate(point.date)}
                  </text>
                );
              })}

              {visibleMetricIds.includes('sales')
                ? salesTicks.map((tick) => (
                    <text
                      key={`sales-axis-${tick}`}
                      x={-12}
                      y={yForSales(tick) + 4}
                      textAnchor="end"
                      className="fill-violet-600 text-[11px]"
                    >
                      {formatPerformanceAxisValue(tick, 'currency')}
                    </text>
                  ))
                : null}

              {visibleMetricIds.includes('spend')
                ? spendTicks.map((tick) => (
                    <text
                      key={`spend-axis-${tick}`}
                      x={-42}
                      y={yForSpend(tick) + 4}
                      textAnchor="end"
                      className="fill-amber-500 text-[11px]"
                    >
                      {formatPerformanceAxisValue(tick, 'currency')}
                    </text>
                  ))
                : null}

              {visibleMetricIds.includes('orders')
                ? ordersTicks.map((tick) => (
                    <text
                      key={`orders-axis-${tick}`}
                      x={plotWidth + 12}
                      y={yForOrders(tick) + 4}
                      textAnchor="start"
                      className="fill-teal-600 text-[11px]"
                    >
                      {formatPerformanceAxisValue(tick, 'count')}
                    </text>
                  ))
                : null}

              {hoveredPoint && hoveredIndex !== null ? (
                <circle
                  cx={xForIndex(hoveredIndex)}
                  cy={yForMetric(
                    visibleMetricIds[0] ?? 'sales',
                    hoveredPoint[visibleMetricIds[0] ?? 'sales']
                  )}
                  r={4}
                  fill={performanceMetricDef(visibleMetricIds[0] ?? 'sales').color}
                  stroke="white"
                  strokeWidth={2}
                />
              ) : null}
            </g>

            {hoveredPoint && tooltipPos ? (
              <foreignObject x={tooltipPos.x} y={tooltipPos.y} width={TOOLTIP_WIDTH} height={TOOLTIP_HEIGHT}>
                <div className="rounded-lg border border-border bg-surface/95 px-3 py-2 text-xs shadow-md backdrop-blur-sm">
                  <div className="font-medium text-foreground">
                    {formatPerformanceTooltipDate(hoveredPoint.date)}
                  </div>
                  <div className="mt-1 space-y-0.5 text-foreground-muted">
                    {visibleMetrics.map((metric) => (
                      <div key={metric.id} className="flex items-center justify-between gap-3">
                        <span style={{ color: metric.color }}>{metric.label}</span>
                        <span className="font-medium tabular-nums text-foreground">
                          {formatPerformanceTooltipValue(hoveredPoint[metric.id], metric.format)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </foreignObject>
            ) : null}
          </svg>

          <div className="mt-2 flex flex-wrap items-center justify-center gap-5 text-xs text-foreground-muted">
            {visibleMetrics.map((metric) => (
              <div key={metric.id} className="inline-flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: metric.color }}
                  aria-hidden
                />
                {metric.label}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
